import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Card, CardHeader, CardTitle, CardContent,
  Button, Input, Modal, ModalFooter, Badge, Alert, AlertDescription,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
  Select, Textarea, LastUpdated, EarningsTrendChart,
} from '../components/ui';
import { formatCurrency, formatDate, formatNumber } from '../lib/utils';
import { 
  Plus, Trash2, Settings, Save, Link as LinkIcon,
  Eye, Database, AlertCircle, CheckCircle, Unplug, ChevronLeft, ChevronRight, ShieldCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonPublisherPlatform } from '../components/ui/Skeleton';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';

const CURRENCIES = [
  { code: 'USD', symbol: '$' },
  { code: 'NGN', symbol: '₦' },
  { code: 'GBP', symbol: '£' },
  { code: 'EUR', symbol: '€' },
];

const getMonthLabel = (date) => date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const getMonthRange = (year, month) => ({
  start: new Date(year, month, 1).toISOString().split('T')[0],
  end: new Date(year, month + 1, 0).toISOString().split('T')[0]
});

export default function AdManager() {
  const { user, isPro, isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ network_code: '', json_key: '', currency: 'USD', revenue_share: 68 });
  const [dailyData, setDailyData] = useState([]);
  const [urlData, setUrlData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [fetchingUrls, setFetchingUrls] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0);
  
  const [showSettings, setShowSettings] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [todayRow,     setTodayRow]     = useState(null);
  const [yesterdayRow, setYesterdayRow] = useState(null);
  const [manualForm, setManualForm] = useState({ date: new Date().toISOString().split('T')[0], gross_revenue: '', impressions: '', clicks: '' });

  // Track visibility and loading state
  const lastVisibleTime = useRef(Date.now());
  const isLoadingRef = useRef(false);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceHidden = now - lastVisibleTime.current;
        
        // If we've been hidden for more than 30 seconds, reload silently
        if (timeSinceHidden > 30000 && !isLoadingRef.current) {
          setFetching(false);
          setFetchingUrls(false);
          
          setTimeout(() => {
            if (!isLoadingRef.current) {
              loadDataSilent();
            }
          }, 500);
        }
        
        lastVisibleTime.current = now;
      } else {
        lastVisibleTime.current = Date.now();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const getViewMonth = useCallback(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() - monthOffset, 1);
  }, [monthOffset]);

  // Silent reload (no spinner)
  const loadDataSilent = useCallback(async () => {
    if (!user || isLoadingRef.current) return;
    
    try {
      const { data: s, error: sErr } = await supabase.from('admanager_settings').select('*').eq('user_id', user.id).maybeSingle();
      if (!sErr?.message?.includes('AbortError') && !sErr?.message?.includes('Lock')) {
        if (s) {
          setSettings(s);
          setForm({ network_code: s.network_code || '', json_key: s.json_key || '', currency: s.currency || 'USD', revenue_share: s.revenue_share || 68 });
        }
      }

      const vm = getViewMonth();
      const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
      
      const { data: daily, error: dErr } = await supabase.from('admanager_daily').select('*').eq('user_id', user.id).gte('date', start).lte('date', end).order('date', { ascending: false });
      if (!dErr?.message?.includes('AbortError') && !dErr?.message?.includes('Lock') && daily) {
        setDailyData(daily);
      }

      if (isPro || isAdmin) {
        const { data: urls, error: uErr } = await supabase.from('admanager_urls').select('url, revenue').eq('user_id', user.id).gte('date', start).lte('date', end);
        if (!uErr?.message?.includes('AbortError') && !uErr?.message?.includes('Lock') && urls) {
          const map = {};
          urls.forEach(r => { map[r.url] = (map[r.url] || 0) + (Number(r.revenue) || 0); });
          setUrlData(Object.entries(map).map(([url, revenue]) => ({ url, revenue })).sort((a, b) => b.revenue - a.revenue).slice(0, 5));
        }
      }
    } catch (e) {
      // Silently ignore abort/lock errors
      if (!e.message?.includes('AbortError') && !e.message?.includes('Lock')) {
        console.error('Silent load error:', e);
      }
    }
  }, [user, getViewMonth, isPro, isAdmin]);

  const loadData = useCallback(async () => {
    if (!user) return;
    
    isLoadingRef.current = true;
    setLoading(true);
    
    try {
      const { data: s, error: sErr } = await supabase.from('admanager_settings').select('*').eq('user_id', user.id).maybeSingle();
      
      // Ignore abort/lock errors
      if (sErr && !sErr.message?.includes('AbortError') && !sErr.message?.includes('Lock')) {
        console.error('Settings error:', sErr);
      }
      
      setSettings(s);
      if (s) setForm({ network_code: s.network_code || '', json_key: s.json_key || '', currency: s.currency || 'USD', revenue_share: s.revenue_share || 68 });

      const vm = getViewMonth();
      const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
      
      const { data: daily, error: dErr } = await supabase.from('admanager_daily').select('*').eq('user_id', user.id).gte('date', start).lte('date', end).order('date', { ascending: false });
      
      if (dErr && !dErr.message?.includes('AbortError') && !dErr.message?.includes('Lock')) {
        console.error('Daily error:', dErr);
      }
      setDailyData(daily || []);

      // Always fetch today + yesterday regardless of which month is being viewed
      const todayStr     = new Date().toISOString().split('T')[0];
      const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      const { data: recentRows } = await supabase
        .from('admanager_daily')
        .select('date, net_revenue, gross_revenue, impressions, clicks, ecpm, ctr')
        .eq('user_id', user.id)
        .in('date', [todayStr, yesterdayStr]);
      if (recentRows) {
        setTodayRow(recentRows.find(r => r.date === todayStr) || null);
        setYesterdayRow(recentRows.find(r => r.date === yesterdayStr) || null);
      }

      if (isPro || isAdmin) {
        const { data: urls, error: uErr } = await supabase.from('admanager_urls').select('url, revenue').eq('user_id', user.id).gte('date', start).lte('date', end);
        
        if (uErr && !uErr.message?.includes('AbortError') && !uErr.message?.includes('Lock')) {
          console.error('URLs error:', uErr);
        }
        
        if (urls) {
          const map = {};
          urls.forEach(r => { map[r.url] = (map[r.url] || 0) + (Number(r.revenue) || 0); });
          setUrlData(Object.entries(map).map(([url, revenue]) => ({ url, revenue })).sort((a, b) => b.revenue - a.revenue).slice(0, 5));
        }
      }
    } catch (e) {
      // Don't show error for abort/lock errors
      if (!e.message?.includes('AbortError') && !e.message?.includes('Lock')) {
        console.error('Load error:', e);
        toast.error('Load failed');
      }
    }
    
    setLoading(false);
    isLoadingRef.current = false;
  }, [user, getViewMonth, isPro, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  // Re-fetch when user returns to this tab after being away
  useVisibilityRefresh(loadData);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (saving) return;
    
    if (!form.network_code || !form.json_key) { toast.error('Network Code and JSON Key required'); return; }
    try { JSON.parse(form.json_key); } catch { toast.error('Invalid JSON'); return; }
    
    setSaving(true);
    try {
      const payload = { 
        user_id: user.id, 
        network_code: form.network_code.trim(), 
        json_key: form.json_key.trim(), 
        currency: form.currency, 
        revenue_share: parseFloat(form.revenue_share) || 68, 
        is_connected: true, 
        updated_at: new Date().toISOString() 
      };
      
      // Check if settings already exist (by querying fresh)
      const { data: existing } = await supabase
        .from('admanager_settings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (existing?.id) {
        await supabase.from('admanager_settings').update(payload).eq('id', existing.id);
      } else {
        await supabase.from('admanager_settings').insert(payload);
      }
      
      // Update onboarding progress
      await supabase
        .from('onboarding_progress')
        .update({ admanager_connected: true, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      
      toast.success('Saved!');
      setSaving(false);
      setShowSettings(false);
      await loadData();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Save failed');
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect?')) return;
    await supabase.from('admanager_settings').update({ is_connected: false, json_key: null }).eq('user_id', user.id);
    toast.success('Disconnected'); loadData();
  };

  const handleFetch = async (type) => {
    if (!settings?.is_connected) { toast.error('Connect first'); return; }
    type === 'daily' ? setFetching(true) : setFetchingUrls(true);
    const tid = toast.loading(type === 'daily' ? 'Fetching last 3 days...' : 'Fetching URLs...');
    try {
      const res = await fetch('/api/fetch-admanager', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: user.id, type }) });
      const data = await res.json();
      data.success ? toast.success(data.message, { id: tid }) : toast.error(data.error, { id: tid });
      if (data.success) loadData();
    } catch (e) { toast.error(e.message, { id: tid }); }
    setFetching(false); setFetchingUrls(false);
  };

  const handleManual = async (e) => {
    e.preventDefault();
    if (!manualForm.date || !manualForm.gross_revenue) { toast.error('Date and revenue required'); return; }
    setSaving(true);
    const gross = parseFloat(manualForm.gross_revenue) || 0;
    const net = gross * ((settings?.revenue_share || 68) / 100);
    const impr = parseInt(manualForm.impressions) || 0;
    const clicks = parseInt(manualForm.clicks) || 0;
    await supabase.from('admanager_daily').upsert({ user_id: user.id, date: manualForm.date, gross_revenue: gross, net_revenue: net, impressions: impr, clicks, ecpm: impr > 0 ? (net / impr) * 1000 : 0, ctr: impr > 0 ? (clicks / impr) * 100 : 0, source: 'manual' }, { onConflict: 'user_id,date' });
    toast.success('Saved!'); setShowManual(false); setManualForm({ date: new Date().toISOString().split('T')[0], gross_revenue: '', impressions: '', clicks: '' }); loadData();
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;
    await supabase.from('admanager_daily').delete().eq('id', selectedEntry.id);
    toast.success('Deleted'); setShowDelete(false); setSelectedEntry(null); loadData();
  };

  const totalNet = dailyData.reduce((s, d) => s + (Number(d.net_revenue) || 0), 0);
  const totalGross = dailyData.reduce((s, d) => s + (Number(d.gross_revenue) || 0), 0);
  const totalImpr = dailyData.reduce((s, d) => s + (Number(d.impressions) || 0), 0);
  const totalClicks = dailyData.reduce((s, d) => s + (Number(d.clicks) || 0), 0);
  const avgEcpm = totalImpr > 0 ? (totalNet / totalImpr) * 1000 : 0;

  const today = new Date().toISOString().split('T')[0];
  const monthLabel = getMonthLabel(getViewMonth());
  const isConnected = settings?.is_connected;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Google Ad Manager</h1>
          <p className="text-muted-foreground">Track your Ad Manager revenue</p>
          {settings?.last_fetch && <LastUpdated value={settings.last_fetch} className="mt-2" />}
        </div>
        <Button variant="outline" onClick={() => setShowSettings(true)}><Settings className="h-4 w-4 mr-2" />Settings</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {isConnected ? <CheckCircle className="h-5 w-5 text-green-500" /> : <AlertCircle className="h-5 w-5 text-yellow-500" />}
              <div>
                <p className="font-medium">{isConnected ? 'Connected' : 'Not Connected'}</p>
                {isConnected && <p className="text-sm text-muted-foreground">Network: {settings.network_code} • Share: {settings.revenue_share}%{settings.last_fetch && ` • Last: ${formatDate(settings.last_fetch)}`}</p>}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap justify-start sm:justify-end">
              {!isConnected ? <Button size="sm" onClick={() => setShowSettings(true)}>Connect</Button> : (
                <>
                  <Button size="sm" onClick={() => handleFetch('daily')} disabled={fetching || fetchingUrls}><Database className={`h-4 w-4 mr-2 ${fetching ? 'animate-pulse' : ''}`} />{fetching ? 'Fetching...' : 'Fetch Daily'}</Button>
                  {(isPro || isAdmin) && <Button size="sm" variant="outline" onClick={() => handleFetch('urls')} disabled={fetching || fetchingUrls}><LinkIcon className={`h-4 w-4 mr-2 ${fetchingUrls ? 'animate-pulse' : ''}`} />{fetchingUrls ? 'Fetching...' : 'Fetch URLs'}</Button>}
                  <Button size="sm" variant="ghost" onClick={handleDisconnect}><Unplug className="h-4 w-4" /></Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">Today</p><p className="text-2xl font-bold">{formatCurrency(todayRow?.net_revenue || 0)}</p><p className="text-xs text-muted-foreground mt-0.5">{todayRow ? `${formatNumber(todayRow.impressions || 0)} impr` : 'No data yet'}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">Yesterday</p><p className="text-2xl font-bold">{formatCurrency(yesterdayRow?.net_revenue || 0)}</p><p className="text-xs text-muted-foreground mt-0.5">{yesterdayRow ? `${formatNumber(yesterdayRow.impressions || 0)} impr` : 'No data yet'}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">{monthLabel}</p><p className="text-2xl font-bold">{formatCurrency(totalNet)}</p><p className="text-xs text-muted-foreground">Gross: {formatCurrency(totalGross)}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">Avg eCPM</p><p className="text-2xl font-bold">${avgEcpm.toFixed(2)}</p><p className="text-xs text-muted-foreground">{formatNumber(totalImpr)} impr</p></CardContent></Card>
      </div>

      {/* 30-day earnings trend */}
      {isConnected && (
        <EarningsTrendChart
          userId={user?.id}
          table="admanager_daily"
          label="Ad Manager net earnings"
          days={30}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          <Card className="overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle>Daily Performance</CardTitle>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setMonthOffset(Math.min(2, monthOffset + 1))} disabled={monthOffset >= 2}><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm font-medium min-w-[130px] text-center">{monthLabel}</span>
                  <Button size="sm" variant="outline" onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))} disabled={monthOffset <= 0}><ChevronRight className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => setShowManual(true)}><Plus className="h-4 w-4 mr-1" />Add</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? <div className="p-6"><SkeletonPublisherPlatform /></div> : (
                <div className="overflow-x-auto w-full">
                  <table className="min-w-[580px] w-full text-sm">
                    <thead className="border-b">
                      <tr>
                        {['Date','Impr.','Clicks','eCPM','CTR','Net Rev.','Source',''].map(h => (
                          <th key={h} className={`px-4 py-3 text-xs font-semibold text-muted-foreground whitespace-nowrap ${h && h !== '' ? 'text-left' : ''}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {dailyData.length === 0 ? (
                        <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No data for {monthLabel}</td></tr>
                      ) : dailyData.map(d => (
                        <tr key={d.id} className="hover:bg-muted/30">
                          <td className="px-4 py-3 font-medium whitespace-nowrap">{formatDate(d.date)}</td>
                          <td className="px-4 py-3 text-right">{formatNumber(d.impressions)}</td>
                          <td className="px-4 py-3 text-right">{formatNumber(d.clicks)}</td>
                          <td className="px-4 py-3 text-right">${(d.ecpm || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">{(d.ctr || 0).toFixed(2)}%</td>
                          <td className="px-4 py-3 text-right font-medium text-green-600">{formatCurrency(d.net_revenue)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${d.source === 'api' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {d.source}
                            </span>
                          </td>
                          <td className="px-2 py-3">
                            <button onClick={() => { setSelectedEntry(d); setShowDelete(true); }} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg">Top URLs</CardTitle>{(isPro || isAdmin) && urlData.length > 0 && <Link to="/ad-manager/urls"><Button size="sm" variant="ghost"><Eye className="h-4 w-4 mr-1" />All</Button></Link>}</div></CardHeader>
            <CardContent>
              {!isPro && !isAdmin ? <div className="text-center py-6"><p className="text-sm text-muted-foreground mb-3">Pro feature</p><Link to="/subscribe"><Button size="sm">Upgrade</Button></Link></div>
              : urlData.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No URL data</p>
              : <div className="space-y-2">{urlData.map((u, i) => <div key={i} className="flex justify-between py-2 border-b last:border-0"><span className="text-sm truncate max-w-[150px]">{u.url}</span><span className="font-medium text-green-600">{formatCurrency(u.revenue)}</span></div>)}</div>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-lg">Summary</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Days</span><span>{dailyData.length}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Impressions</span><span>{formatNumber(totalImpr)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Clicks</span><span>{formatNumber(totalClicks)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Share</span><span>{settings?.revenue_share || 68}%</span></div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Modal isOpen={showSettings} onClose={() => setShowSettings(false)} title="Ad Manager Settings" size="lg">
        <form onSubmit={handleSaveSettings}>
          <div className="space-y-4">
            <Input label="Network Code *" value={form.network_code} onChange={e => setForm({ ...form, network_code: e.target.value })} required />
            <div><label className="text-sm font-medium">JSON Key *</label><Textarea value={form.json_key} onChange={e => setForm({ ...form, json_key: e.target.value })} rows={6} className="mt-1 font-mono text-xs" required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium">Currency</label><Select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="mt-1">{CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}</Select></div>
              <Input label="Revenue Share (%)" type="number" min="1" max="100" value={form.revenue_share} onChange={e => setForm({ ...form, revenue_share: e.target.value })} />
            </div>
            <Alert><AlertDescription>Fetches last 3 days. Data kept for 3 months.</AlertDescription></Alert>
          </div>
          <ModalFooter><Button variant="outline" type="button" onClick={() => setShowSettings(false)}>Cancel</Button><Button type="submit" loading={saving}><Save className="h-4 w-4 mr-2" />Save</Button></ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={showManual} onClose={() => setShowManual(false)} title="Add Entry">
        <form onSubmit={handleManual}>
          <div className="space-y-4">
            <Input label="Date" type="date" value={manualForm.date} onChange={e => setManualForm({ ...manualForm, date: e.target.value })} required />
            <Input label="Gross Revenue ($)" type="number" step="0.01" value={manualForm.gross_revenue} onChange={e => setManualForm({ ...manualForm, gross_revenue: e.target.value })} required />
            <Input label="Impressions" type="number" value={manualForm.impressions} onChange={e => setManualForm({ ...manualForm, impressions: e.target.value })} />
            <Input label="Clicks" type="number" value={manualForm.clicks} onChange={e => setManualForm({ ...manualForm, clicks: e.target.value })} />
          </div>
          <ModalFooter><Button variant="outline" type="button" onClick={() => setShowManual(false)}>Cancel</Button><Button type="submit" loading={saving}>Save</Button></ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={showDelete} onClose={() => { setShowDelete(false); setSelectedEntry(null); }} title="Delete">
        <Alert variant="warning"><AlertDescription>Delete entry for {selectedEntry && formatDate(selectedEntry.date)}?</AlertDescription></Alert>
        <ModalFooter><Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button><Button variant="destructive" onClick={handleDelete}>Delete</Button></ModalFooter>
      </Modal>

      {/* HapiGuard banner */}
      <div className="rounded-xl border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/70 px-4 py-3 flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-emerald-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">HapiGuard click protection</p>
          <p className="text-xs text-emerald-800 dark:text-emerald-300">Protect your AdSense and Ad Manager revenue from invalid clicks and click bombing in real time.</p>
        </div>
        <Link to="/hapiguard" className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors whitespace-nowrap">
          HapiGuard →
        </Link>
      </div>
    </div>
  );
}
